"use client";

import { Storage } from "@lib/storage";
import React, { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { log } from "@lib/utils/log";
const DEPRECATED_KEY_PREFIXES = ["region-", "song-state-", "swr-cache"];
type ContextValue = {
  getItem: <T>(key: string, deprecatedKey?: string) => Promise<T | null>;
  setItem: <T>(key: string, item: T) => void;
  removeItem: (key: string) => void;
  clear: () => void;
};
export const StorageContext = createContext<ContextValue>({
  getItem: <_ extends any,>(_: string, __?: string) => {
    throw new Error("StorageContext not initialized");
  },
  setItem: <T extends any,>(_: string, __: T) => {},
  removeItem: (_: string) => {},
  clear: () => {}
});
type Props = {
  children: ReactNode;
};
export function StorageProvider({
  children
}: Props) {
  const [store] = useState<Storage>(() => new Storage());
  const getItem = useCallback(async <T extends any,>(key: string, deprecatedKey?: string): Promise<T | null> => {
    log("StorageProvider - getItem", key);
    return await store.getItem<T>(key, deprecatedKey);
  }, [store]);
  const setItem = useCallback(async <T extends any,>(key: string, item: T) => {
    log("StorageProvider - Setting item", key);
    await store.setItem<T>(key, item);
  }, [store]);
  const removeItem = useCallback(async (key: string) => {
    log("StorageProvider - Removing item", key);
    await store.removeItem(key);
  }, [store]);
  const clear = useCallback(async () => {
    log("StorageProvider - Clearing storage");
    await store.clear();
  }, [store]);
  useEffect(() => {
    const clearDeprecatedKeys = async () => {
      const keys = await store.keys();
      for (const key of keys) {
        if (DEPRECATED_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
          await store.removeItem(key);
        }
      }
    };

    // Defer cleanup to idle time to avoid blocking the main thread during page load.
    // This reduces Total Blocking Time (TBT) by running IndexedDB iteration when the browser is idle.
    if (typeof requestIdleCallback !== 'undefined') {
      const idleId = requestIdleCallback(() => clearDeprecatedKeys());
      return () => cancelIdleCallback(idleId);
    } else {
      // Fallback for Safari: use setTimeout with a delay
      const timeoutId = setTimeout(() => clearDeprecatedKeys(), 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [store]);
  return <StorageContext.Provider value={{
    getItem,
    setItem,
    removeItem,
    clear
  }} data-sentry-element="StorageContext.Provider" data-sentry-component="StorageProvider" data-sentry-source-file="StorageProvider.tsx">{children}</StorageContext.Provider>;
}
export const useStorage = () => {
  const context = useContext(StorageContext);
  if (!context) {
    throw new Error('useStorage must be used within a StorageProvider');
  }
  return context;
};